import { type FC, useRef, useState } from "react";

import {
  type DraftingSectionStatus,
  DraftingSectionStatusEnum,
  DraftingSourceEnum,
} from "@app_schema";

import { Button } from "@styled/button";
import { CitationLine } from "@styled/citation_line";
import { CompactTextArea } from "@styled/compact_text_area";
import { SectionNotes } from "@styled/section_notes";
import { SectionRow } from "@styled/section_row";
import { SectionStatusPill } from "@styled/section_status_pill";
import { SourcesPicker } from "@styled/sources_picker";

import { DraftingSectionEditor } from "./drafting_section_editor";
import { DraftingSectionMenu } from "./drafting_section_menu";
import { DraftingSectionRemove } from "./drafting_section_remove";
import { DraftingSectionReplace } from "./drafting_section_replace";
import {
  CitationsOutside,
  SourcesFooter,
  SupportingDocumentSelect,
  citationsLine,
  sourceOptions,
  toggled,
} from "./drafting_sources";
import {
  type DraftingSections,
  useDraftingSectionsOf,
} from "./use_drafting_sections";

type Section = DraftingSections["sections"][number];

// A section's status and line in the chat open.
export type SectionInChat = Pick<
  DraftingSectionStatus,
  "status" | "statusLine" | "alert" | "citations"
>;

// One of the case's Drafting sections: asking for it (first whether to replace
// his edits, if any), what it reads, its status (`inChat`, else its own), and
// the box for his notes when it reads them.
export const DraftingSectionItem: FC<{
  section: Section;
  inChat?: SectionInChat;
  disabled: boolean;
  onAsk(question: string): void;
}> = ({ section, inChat, disabled, onAsk }) => {
  const drafting = useDraftingSectionsOf();
  const [notes, setNotes] = useState(section.notes ?? "");
  const [editing, setEditing] = useState<{ top: number; left: number }>();
  const [removing, setRemoving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const status = inChat?.status ?? section.status;
  const reads = (source: DraftingSourceEnum) =>
    section.sources.includes(source);
  const draft = () => onAsk(`Draft ${section.name}`);
  const ask = () => {
    if (section.edited) setReplacing(true);
    else draft();
  };

  const edit = () => {
    const rect = rowRef.current?.getBoundingClientRect();
    if (rect) setEditing({ top: rect.bottom + 4, left: rect.left });
  };

  return (
    <div ref={rowRef}>
      <SectionRow
        name={section.name}
        tag={section.edited ? "Edited by you" : undefined}
        waiting={status === DraftingSectionStatusEnum.Waiting}
        line={inChat ? inChat.statusLine : section.statusLine}
        alert={inChat ? inChat.alert : section.alert}
        flag={
          inChat &&
          inChat.citations.length > 0 && (
            <CitationLine
              text={citationsLine(inChat.citations, section.readsLabel)}
              list={
                inChat.citations.length > 1 && (
                  <CitationsOutside
                    citations={inChat.citations}
                    reads={section.readsLabel}
                  />
                )
              }
            />
          )
        }
        status={<SectionStatusPill status={status} />}
        menu={
          <DraftingSectionMenu
            onTemplate={drafting.onTemplate}
            onEdit={edit}
            onRemove={() => setRemoving(true)}
          />
        }
        disabled={disabled}
        onAsk={section.his ? undefined : ask}
        sources={
          !section.his && (
            <SourcesPicker
              label={section.sourcesLabel}
              title={`${section.name} reads from`}
              disabled={drafting.saving}
              options={sourceOptions(section.sources)}
              onToggle={(value) =>
                void drafting.setSources(
                  section.id,
                  toggled(section.sources, value as DraftingSourceEnum),
                  section.supportingDocumentId,
                )
              }
              extra={
                reads(DraftingSourceEnum.Supporting) && (
                  <SupportingDocumentSelect
                    documents={drafting.supportingDocuments}
                    value={section.supportingDocumentId}
                    onChange={(id) =>
                      void drafting.setSources(
                        section.id,
                        [...section.sources],
                        id,
                      )
                    }
                  />
                )
              }
              footer={
                <SourcesFooter
                  note={section.sourceNote}
                  onTemplate={drafting.onTemplate}
                />
              }
            />
          )
        }
      />
      {reads(DraftingSourceEnum.Notes) && (
        <SectionNotes
          label="Your notes · this case only"
          line="Your notes are used as you wrote them."
          field={
            <CompactTextArea
              aria-label={`Your notes for ${section.name}`}
              placeholder="Type your notes for this section"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              onBlur={() => void drafting.setNotes(section.id, notes)}
            />
          }
          action={
            <Button
              type="button"
              color="violet"
              size="sm"
              disabled={disabled || notes.trim() === ""}
              onClick={() =>
                void drafting.setNotes(section.id, notes).then(ask)
              }
            >
              Draft this section
            </Button>
          }
        />
      )}
      {editing && (
        <DraftingSectionEditor
          target={{ section }}
          top={editing.top}
          left={editing.left}
          onClose={() => setEditing(undefined)}
        />
      )}
      {replacing && (
        <DraftingSectionReplace
          section={section}
          onReplace={draft}
          onClose={() => setReplacing(false)}
        />
      )}
      {removing && (
        <DraftingSectionRemove
          section={section}
          onClose={() => setRemoving(false)}
        />
      )}
    </div>
  );
};
